import React, { useRef, useState, useEffect } from 'react';

interface SimpleDraggableProps {
  position: { x: number; y: number };
  onDrag: (e: MouseEvent | TouchEvent, data: { x: number; y: number; deltaX: number; deltaY: number }) => void;
  onStart?: () => void;
  onStop?: () => void;
  handle?: string;
  children: React.ReactNode;
}

const SimpleDraggable: React.FC<SimpleDraggableProps> = ({
  position,
  onDrag,
  onStart,
  onStop,
  handle = '.drag-handle',
  children
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState(position);

  // Check if device supports touch
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const handleMouseDown = (e: React.MouseEvent) => {
    // Skip if touch device (will use touch events instead)
    if (isTouchDevice) return;

    // Check if we clicked on the drag handle
    const target = e.target as HTMLElement;
    const handleElement = elementRef.current?.querySelector(handle);

    if (!handleElement?.contains(target)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPosition(position);

    if (onStart) {
      onStart();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // On touch devices, allow dragging from anywhere on the block
    const target = e.target as HTMLElement;

    // Don't allow dragging if clicking on a button or input
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    e.stopPropagation();

    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY });
    setInitialPosition(position);

    if (onStart) {
      onStart();
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const newPosition = {
        x: initialPosition.x + deltaX,
        y: initialPosition.y + deltaY
      };

      onDrag(e, {
        x: newPosition.x,
        y: newPosition.y,
        deltaX,
        deltaY
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault(); // Prevent scrolling while dragging
      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStart.x;
      const deltaY = touch.clientY - dragStart.y;

      const newPosition = {
        x: initialPosition.x + deltaX,
        y: initialPosition.y + deltaY
      };

      onDrag(e, {
        x: newPosition.x,
        y: newPosition.y,
        deltaX,
        deltaY
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (onStop) {
        onStop();
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      if (onStop) {
        onStop();
      }
    };

    if (isTouchDevice) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);
    } else {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    // Change cursor (only for non-touch devices)
    if (!isTouchDevice) {
      document.body.style.cursor = 'grabbing';
    }
    document.body.style.userSelect = 'none';

    return () => {
      if (isTouchDevice) {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
      } else {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragStart, initialPosition, onDrag, onStop, isTouchDevice]);

  return (
    <div
      ref={elementRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        transform: 'translate(0, 0)', // Ensure proper positioning
        touchAction: 'none' // Prevent default touch behaviors like scrolling
      }}
    >
      {children}
    </div>
  );
};

export default SimpleDraggable;